import React, { useRef } from "react";
import { motion, useMotionValue, useTransform, useSpring, useScroll } from "framer-motion";
import { MousePointer2, Scan, DollarSign, Lock, Quote, ArrowUpRight, CheckCircle2 } from "lucide-react";

// --- HELPER: BOOTH REVEAL ANIMATION ---
const BoothReveal = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  // Images start hidden behind the main image (0%) and slide out as we scroll
  // yTop moves UP (-95%), yBottom moves DOWN (+95%) to fully clear the main card
  // Added slight rotation and X-drift for "sliding on table" feel
  // Faster animation: reduced scroll range from [0.1, 0.8] to [0.1, 0.5]
  const yTop = useTransform(scrollYProgress, [0.1, 0.5], ["0%", "-95%"]);
  const yBottom = useTransform(scrollYProgress, [0.1, 0.5], ["0%", "95%"]);

  const rotateTop = useTransform(scrollYProgress, [0.1, 0.5], ["0deg", "-6deg"]);
  const rotateBottom = useTransform(scrollYProgress, [0.1, 0.5], ["0deg", "6deg"]);

  const xTop = useTransform(scrollYProgress, [0.1, 0.5], ["0%", "-5%"]);
  const xBottom = useTransform(scrollYProgress, [0.1, 0.5], ["0%", "5%"]);

  return (
    <div ref={ref} className="relative w-full max-w-[140px] lg:max-w-[160px] mx-auto aspect-[3/4]">
      {/* Top Card (1) - Emerges Upwards */}
      <motion.div style={{ y: yTop, rotate: rotateTop, x: xTop }} className="absolute inset-0 z-0 origin-bottom-right">
        <img
          src="/assets/BOOTH%20CAD%201.jpeg"
          alt="Booth Cad 1"
          className="w-full h-full object-cover border-[6px] border-white shadow-xl shadow-black/20"
        />
      </motion.div>

      {/* Bottom Card (3) - Emerges Downwards */}
      <motion.div style={{ y: yBottom, rotate: rotateBottom, x: xBottom }} className="absolute inset-0 z-0 origin-top-left">
        <img
          src="/assets/BOOTH%20CAD%203.jpeg"
          alt="Booth Cad 3"
          className="w-full h-full object-cover border-[6px] border-white shadow-xl shadow-black/20"
        />
      </motion.div>

      {/* Middle Card (2) - Main Visible - Static on top */}
      <div className="absolute inset-0 z-10">
        <img
          src="/assets/BOOTH%20CAD%202.jpeg"
          alt="Booth Cad 2"
          className="w-full h-full object-cover border-[6px] border-white bg-[#009EE0] shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)]"
        />
      </div>
    </div>
  );
};

// --- HELPER: MULTIPLAYER CURSOR ---
const Cursor = ({ label, x, y, color = "#FF3333" }) => (
  <div
    className="absolute z-20 flex items-start gap-2"
    style={{ left: `${x}%`, top: `${y}%` }}
  >
    <MousePointer2 size={24} fill={color} className="text-white drop-shadow-md" />
    <div className="bg-white text-[#009EE0] px-2 py-1 text-[10px] font-mono font-bold uppercase tracking-wider shadow-sm -mt-1">
      {label}
    </div>
  </div>
);

// --- HELPER: TESTIMONIAL CARD ---
const TestimonialCard = ({ quote, author, role, color, rotation }) => (
  <motion.div
    whileHover={{ scale: 1.05, rotate: 0, zIndex: 50 }}
    initial={{ rotate: rotation }}
    transition={{ type: "spring", stiffness: 300, damping: 20 }}
    className={`p-6 shadow-[0_10px_30px_rgba(0,0,0,0.1)] max-w-[800px] cursor-default border-l-4 ${color}`}
  >
    <p className="font-sans text-[15px] leading-tight font-bold mb-3">
      "{quote}"
    </p>
    <div className="flex flex-col">
      <span className="font-mono text-[10px] uppercase font-bold opacity-100">{author}</span>
      <span className="font-mono text-[9px] uppercase opacity-70">{role}</span>
    </div>
  </motion.div>
);

// --- MAIN COMPONENT ---
const ChangeSection = () => {
  const containerRef = useRef(null);

  // --- MOUSE PARALLAX SETUP ---
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    const { innerWidth, innerHeight } = window;
    // Normalize mouse position (-1 to 1)
    const x = (clientX / innerWidth) * 2 - 1;
    const y = (clientY / innerHeight) * 2 - 1;
    mouseX.set(x);
    mouseY.set(y);
  };

  // Create smooth springs from mouse values
  const springConfig = { damping: 25, stiffness: 150 };
  const smoothX = useSpring(mouseX, springConfig);
  const smoothY = useSpring(mouseY, springConfig);

  // Parallax Layers (Front moves more than back)
  const gridBackX = useTransform(smoothX, [-1, 1], [20, -20]);
  const gridBackY = useTransform(smoothY, [-1, 1], [20, -20]);
  const gridFrontX = useTransform(smoothX, [-1, 1], [50, -50]);
  const gridFrontY = useTransform(smoothY, [-1, 1], [50, -50]);

  const gridColor = "bg-[#FF3333]"; // Structural Red

  return (
    <section
      ref={containerRef}
      onMouseMove={handleMouseMove}
      className="relative w-full min-h-[120vh] bg-[#009EE0] text-white overflow-hidden pt-32 px-8 lg:px-16 pb-32"
    >
      {/* ========================================================
            THE SWISS GRID
           ======================================================== */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-hidden">
        <motion.div style={{ x: gridBackX, y: gridBackY, scale: 1.05 }} className="absolute inset-0 w-full h-full max-w-[1600px] mx-auto opacity-60">
          <div className={`absolute top-0 bottom-0 left-[23%] w-[1px] ${gridColor}`} />
          <div className={`absolute top-0 bottom-0 left-[64%] w-[1px] ${gridColor}`} />
          <div className={`absolute top-0 bottom-0 left-[74.9%] w-[1px] ${gridColor}`} />
          <div className={`absolute left-0 right-0 top-[20%] h-[1px] ${gridColor}`} />
          <div className={`absolute left-0 right-0 top-[50%] h-[1px] ${gridColor}`} />
          <div className={`absolute left-0 right-0 top-[80%] h-[1px] ${gridColor}`} />
        </motion.div>
        <motion.div style={{ x: gridFrontX, y: gridFrontY, scale: 1.1 }} className="absolute inset-0 w-full h-full max-w-[1600px] mx-auto">
          <div className={`absolute top-[-10%] bottom-[-10%] left-[23%] w-[3px] ${gridColor}`} />
          <div className={`absolute top-[-10%] bottom-[-10%] left-[64%] w-[3px] ${gridColor}`} />
          <div className={`absolute top-[-10%] bottom-[-10%] left-[74.9%] w-[3px] ${gridColor}`} />
          <div className={`absolute left-[-10%] right-[-10%] top-[33%] h-[3px] ${gridColor}`} />
          <div className={`absolute left-[-10%] right-[-10%] top-[66%] h-[3px] ${gridColor}`} />
        </motion.div>
      </div>

      {/* ========================================================
            CONTENT LAYER - FLATTENED GRID
           ======================================================== */}
      <div className="relative z-10 max-w-[1600px] mx-auto h-full grid grid-cols-12 gap-y-12 lg:gap-y-8 lg:gap-x-0">

        {/* 1. HEADLINE (the designer's pain) - centred over the demo frame below; "Designer" in the grid-line red */}
        <div className="col-span-12 order-2 pt-12">
          <div className="mx-auto w-full md:w-[80vw] max-w-full">
            {/* Two lines at every width: the only break point is after "Designer"; sizes keep each half on one line */}
            <h2 className="font-sans text-center text-[6.3vw] sm:text-[44px] md:text-[5.6vw] 2xl:text-[88px] leading-[0.95] font-bold uppercase tracking-tight [word-spacing:0.1em] relative z-20">
              You&nbsp;didn’t&nbsp;become&nbsp;a&nbsp;<span className="font-serif italic normal-case pr-[0.08em] text-[#FF3333]" style={{ WebkitTextStroke: "max(2px, 0.04em) #fff", paintOrder: "stroke fill" }}>Designer</span> to&nbsp;make&nbsp;constant&nbsp;revisions
            </h2>
          </div>
        </div>

        {/* 4. FOUNDER QUOTE - beside the testimonials on desktop */}
        <div className="col-span-12 lg:col-span-4 order-5 lg:pl-12 pt-0 lg:pt-4">
          {/* Founder Section */}
          <div className="border-b-[0.5px] z-20 border-[#FF3333] py-4 lg:py-8">
            <div className="flex flex-col gap-4 lg:gap-6">
              <div>
                <p className="font-sans italic text-[4.8vw] sm:text-[24px] lg:text-[1.45vw] 2xl:text-[24px] leading-[1.15] text-white">
                  “Technology&nbsp;democratized&nbsp;productivity. All&nbsp;we&nbsp;have left&nbsp;is&nbsp;your
                  <span className="text-white italic text-[6vw] sm:text-[30px] lg:text-[1.85vw] 2xl:text-[30px] font-bold font-serif mt-1 lg:mt-2">
                    &nbsp;creativity
                  </span>"
                  <span className="block mt-2 lg:mt-4 not-italic font-mono text-[12px] lg:text-[16px] tracking-widest uppercase text-white/80">
                    Juliano&nbsp;Wahab&nbsp;—&nbsp;CEO
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 2. PRODUCT DEMO - the centre 80% of the page, in a hairline frame */}
        <div className="col-span-12 order-3">
          <div className="mx-auto w-full md:w-[80vw] max-w-full p-2 md:p-3 border border-white/60 bg-[#009EE0] shadow-[0_40px_80px_-40px_rgba(0,0,0,0.5)]">
            <video
              ref={(el) => {
                if (!el) return;
                const observer = new IntersectionObserver(
                  (entries) => {
                    entries.forEach((entry) => {
                      if (entry.isIntersecting) {
                        el.play().catch(() => { });
                      } else {
                        el.pause();
                      }
                    });
                  },
                  { threshold: 0.2 }
                );
                observer.observe(el);
              }}
              src="/assets/product-demo.mp4"
              poster="/assets/product-demo-poster.jpg"
              aria-label="Monet product demo: describe your event, get a show floor laid out in your venue, then make each client change in the layout chat"
              className="block w-full h-auto aspect-video object-cover bg-white pointer-events-none"
              muted
              loop
              playsInline
              preload="none"
            />
          </div>
        </div>

        {/* 3. TESTIMONIALS (Row on Desktop, Col on Mobile) */}
        <div className="col-span-12 lg:col-span-8 order-4 pt-0 lg:pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 lg:items-start gap-6 lg:gap-8 max-w-[600px] lg:max-w-none mx-auto lg:mx-0">
            <TestimonialCard
              color="bg-white text-[#009EE0] border-[#FF3333]"
              quote={"Having produced Google Cloud Next for three years, I saw teams spend countless hours and hundreds of thousands of dollars on floor plan iterations and booth assignments. Monet's AI-driven approach is genuinely revolutionary, solving a problem that has plagued event teams\u00a0for\u00a0decades."}
              author="Gianna Gaudini"
              role="Head of Events"
              rotation={-2}
            />
            <TestimonialCard
              color="bg-[#009EE0] text-white border-white border"
              quote={"We spend so much time and budget on early design before execution starts. Monet addresses that friction so delivery stays\u00a0the\u00a0priority"}
              author="Tony Helou"
              role="Head of Event Operations"
              rotation={2}
            />
            {/* <TestimonialCard
              color="bg-[#FF3333] text-white border-black"
              quote="I can give my sales team instant design feedback"
              author="Alan"
              role="BIM Manager"
              rotation={-1}
            /> */}
          </div>
        </div>

      </div>
    </section>
  );
};

export default ChangeSection;